/**
 * @file: AIResponseComposer.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Compose an AI reply to share into a chat. An in-place overlay panel
 * (bottom sheet on mobile, centered card on desktop) — NOT a navigator
 * screen. Enter/exit animated with moti (AnimatePresence + MotiView); the RN
 * Modal stays mounted until the exit animation completes.
 *
 * The user asks a question, the answer streams in (rendered via
 * AIContentRenderer), and a "send to chat" button hands the markdown to
 * `onSend`. Asking again REPLACES the answer — only ever one response shows.
 */

import { AIContentRenderer } from '@euler/app/components/ai/AIContentRenderer';
import { Typography } from '@euler/app/components/typography';
import { BottomPanel } from '@euler/components/BottomPanel';
import { Spinner } from '@euler/components/Spinner';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SSEConnection } from '@euler/lib/request/SSEClient';
import { useServiceFactory } from '@euler/services/factory';
import { ArrowUp, RotateCcw, Send, Sparkles, X } from 'lucide-react-native';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';

const DISABLED_AI_WIDGETS = ['suggested_questions'] as const;

export const AIResponseComposer: FC<{
  visible: boolean;
  onClose: () => void;
  onSend: (markdown: string) => void;
}> = ({ visible, onClose, onSend }) => {
  const { rpx } = useResponsiveContext();
  const { aiAssistantService } = useServiceFactory();

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connRef = useRef<SSEConnection | null>(null);
  const bufferRef = useRef('');
  const flushTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<TextInput>(null);

  const clearFlush = useCallback(() => {
    if (flushTimer.current) {
      clearInterval(flushTimer.current);
      flushTimer.current = null;
    }
  }, []);

  const stopStreaming = useCallback(() => {
    connRef.current?.abort();
    connRef.current = null;
    clearFlush();
  }, [clearFlush]);

  // Reset a fresh session on open; stop any stream on close.
  useEffect(() => {
    if (visible) {
      setQuestion('');
      setAnswer('');
      setAsking(false);
      setError(null);
      bufferRef.current = '';
    } else {
      stopStreaming();
    }
  }, [visible, stopStreaming]);

  useEffect(() => stopStreaming, [stopStreaming]);

  const ask = useCallback(() => {
    const q = question.trim();
    if (!q || asking) return;
    stopStreaming();
    setError(null);
    setAnswer(''); // only one response is ever shown
    setQuestion(''); // clear the input on submit
    bufferRef.current = '';
    setAsking(true);
    // Buffer streamed tokens; flush to state on an interval so a long
    // answer doesn't re-render per token.
    flushTimer.current = setInterval(() => {
      setAnswer(bufferRef.current);
    }, 100);
    connRef.current = aiAssistantService.ask(
      { message: q, sessionType: 'workshop_assistant' },
      {
        onToken: t => {
          bufferRef.current += t;
        },
        onDone: () => {
          clearFlush();
          setAnswer(bufferRef.current);
          setAsking(false);
          connRef.current = null;
        },
        onError: msg => {
          clearFlush();
          setAnswer(bufferRef.current);
          setAsking(false);
          setError(msg);
          connRef.current = null;
        },
      },
    );
  }, [question, asking, aiAssistantService, stopStreaming, clearFlush]);

  // Drop the current answer and return to the ask state (input bar back).
  const askAgain = useCallback(() => {
    stopStreaming();
    setAnswer('');
    bufferRef.current = '';
    setError(null);
    setQuestion('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [stopStreaming]);

  const hasAnswer = answer.trim().length > 0;
  const canSend = hasAnswer && !asking;
  // Input bar only in the ask state — hidden while streaming and once an
  // answer is shown (replaced by the send / ask-again actions).
  const showInput = !asking && !hasAnswer;

  return (
    <BottomPanel visible={visible} onClose={onClose}>
      {/* HEADER — generous top padding so the title clears the panel's
          big rounded top corner. */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: rpx(20),
          paddingTop: rpx(26),
          paddingBottom: rpx(16),
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: '#EEF2F7',
        }}
      >
        <Sparkles color="#3b82f6" size={rpx(20)} />
        <Typography.Text
          size={rpx(19)}
          color="#1E293B"
          style={{
            marginLeft: rpx(8),
            flex: 1,
            fontFamily: FontFamily.NotoSans.Bold,
          }}
        >
          AI 回复
        </Typography.Text>
        <Pressable
          onPress={onClose}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          style={{
            width: rpx(30),
            height: rpx(30),
            borderRadius: rpx(15),
            backgroundColor: '#EFF1F5',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X color="#8E8E93" size={rpx(16)} />
        </Pressable>
      </View>

      {/* BODY */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          padding: rpx(16),
          flexGrow: 1,
          justifyContent: !answer && !asking ? 'center' : 'flex-start',
        }}
        keyboardShouldPersistTaps="handled"
      >
        {answer ? (
          <AIContentRenderer
            content={answer}
            disabledWidgets={[...DISABLED_AI_WIDGETS]}
          />
        ) : asking ? (
          <View
            style={{
              alignItems: 'center',
              paddingVertical: rpx(24),
            }}
          >
            <Spinner size={rpx(22)} color="#3b82f6" />
            <Typography.Text
              size={rpx(13)}
              color="#94A3B8"
              style={{ marginTop: rpx(10) }}
            >
              正在思考…
            </Typography.Text>
          </View>
        ) : (
          <Typography.Text
            size={rpx(14)}
            color="#94A3B8"
            style={{ textAlign: 'center', lineHeight: rpx(21) }}
          >
            向我提任何你想发送到对话的问题
          </Typography.Text>
        )}
        {error ? (
          <Typography.Text
            size={rpx(12)}
            color="#e0463d"
            style={{ marginTop: rpx(10) }}
          >
            {error}
          </Typography.Text>
        ) : null}
      </ScrollView>

      {/* SEND-TO-CHAT + ASK AGAIN (once streaming finishes) */}
      {canSend ? (
        <View
          style={{
            paddingHorizontal: rpx(20),
            paddingTop: rpx(10),
            paddingBottom: rpx(14),
          }}
        >
          <Pressable
            onPress={() => onSend(answer.trim())}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: rpx(8),
              backgroundColor: '#3b82f6',
              borderRadius: rpx(28),
              paddingVertical: rpx(15),
            }}
          >
            <Send color="#fff" size={rpx(17)} />
            <Typography.Text
              size={rpx(16)}
              color="#fff"
              style={{ fontFamily: FontFamily.NotoSans.Bold }}
            >
              发送到对话
            </Typography.Text>
          </Pressable>
          <Pressable
            onPress={askAgain}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: rpx(6),
              marginTop: rpx(10),
              backgroundColor: '#F1F5F9',
              borderRadius: rpx(28),
              paddingVertical: rpx(13),
            }}
          >
            <RotateCcw color="#475569" size={rpx(15)} />
            <Typography.Text
              size={rpx(15)}
              color="#475569"
              style={{ fontFamily: FontFamily.NotoSans.Medium }}
            >
              重新提问
            </Typography.Text>
          </Pressable>
        </View>
      ) : null}

      {/* ASK INPUT (hidden while streaming / showing an answer) */}
      {showInput ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: rpx(8),
            paddingHorizontal: rpx(16),
            paddingTop: rpx(12),
            paddingBottom: rpx(14),
            borderTopWidth: StyleSheet.hairlineWidth,
            borderTopColor: '#EEF2F7',
          }}
        >
          <TextInput
            ref={inputRef}
            value={question}
            onChangeText={setQuestion}
            placeholder="输入问题…"
            placeholderTextColor="#94A3B8"
            returnKeyType="send"
            onSubmitEditing={ask}
            style={{
              flex: 1,
              fontSize: rpx(15),
              fontFamily: FontFamily.NotoSans.Regular,
              color: '#1E293B',
              backgroundColor: '#F1F5F9',
              borderRadius: rpx(20),
              paddingHorizontal: rpx(16),
              paddingVertical: Platform.OS === 'ios' ? rpx(10) : rpx(6),
              minHeight: rpx(40),
            }}
          />
          <Pressable
            onPress={ask}
            disabled={!question.trim()}
            style={{
              width: rpx(40),
              height: rpx(40),
              borderRadius: rpx(20),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: question.trim() ? '#3b82f6' : '#CBD5E1',
            }}
          >
            <ArrowUp color="#fff" size={rpx(20)} strokeWidth={2.5} />
          </Pressable>
        </View>
      ) : null}
    </BottomPanel>
  );
};
